import { Injectable } from '@angular/core';
import { EVENTS_ENDPOINT } from '@app/app.constants';
import {
  EventSourceConnectionState,
  EventSourceService,
  getSubjectLabel,
} from '@app/core/services/event-source.service';
import {
  CommandCallback,
  CommandDefinition,
  CommandHelper,
  CommandResultEntry,
} from '@app/shared/quick-commands';
import { Translatable } from '@app/translate/models/translatable';
import { combineLatest } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class EventSourceCommand implements CommandDefinition<any> {
  alwaysShowSections = true;
  filter = 'automatic' as const;
  icon = 'far fa-lightbulb';
  name = 'event-source';
  private state: EventSourceConnectionState | null = null;

  constructor(private eventSourceService: EventSourceService) {
    this.eventSourceService.state$.subscribe((state) => {
      this.state = state;
    });
  }

  action: CommandCallback<any> = (_: string, [command]: [SupportedCommands]) => {
    if (command === 'stop') {
      this.eventSourceService.stop();
    } else if (command === 'restart') {
      this.eventSourceService.restart();
    } else if (command === 'start') {
      this.eventSourceService.start();
    } else if (command === 'stopReconnecting') {
      this.eventSourceService.stopReconnecting();
    } else if (command === 'showSubjects') {
      return CommandHelper.list$(this.eventSourceService.subjects$, (subjects) => {
        return {
          readonly: true,
          values: subjects.map((subject): CommandResultEntry => {
            return {
              label: getSubjectLabel(subject),
              value: subject,
            };
          }),
        };
      });
    }

    // This counter is necessary to decide whether the result should
    // be replaced or not. The first result needs to be displayed
    // normally and all subsequent results should replace it.
    let count = 0;

    return CommandHelper.list$(
      combineLatest([this.eventSourceService.state$, this.eventSourceService.reconnectingIn$]),
      ([state, reconnectingIn]) => {
        count += 1;

        return {
          infoMessage: {
            key: 'quickCommands.commands.eventSource.info.url',
            parameters: {
              url: `${window.location.protocol}//${window.location.host}${EVENTS_ENDPOINT}`,
            },
          },
          readonly: state === EventSourceConnectionState.Connecting,
          replace: command != null || count > 1,
          sections: [
            {
              label: 'quickCommands.commands.eventSource.sections.actions',
              name: 'actions',
            },
          ],
          statusbar: {
            translationKey: {
              key: 'quickCommands.commands.eventSource.statusbar',
              parametersToTranslate: {
                state: {
                  key: `quickCommands.commands.eventSource.connectionState.${state}`,
                  parameters: {
                    reconnectingIn,
                  },
                },
                icon: `quickCommands.commands.eventSource.statusbar.icon.${state}`,
              },
            },
          },
          values: [...this.getActions(state)],
        };
      }
    );
  };

  label = (): Translatable => {
    return {
      key: 'quickCommands.commands.eventSource',
      parametersToTranslate: {
        connected: `quickCommands.commands.eventSource.connectionState.${this.state}`,
      },
    };
  };

  private getActions(state: EventSourceConnectionState): CommandResultEntry<SupportedCommands>[] {
    const reconnecting: CommandResultEntry = {
      label: 'quickCommands.commands.eventSource.reconnecting',
      value: null,
    };

    const restart: CommandResultEntry = {
      label: 'quickCommands.commands.eventSource.restart',
      section: 'actions',
      value: 'restart',
    };

    const showSubjects: CommandResultEntry = {
      label: 'quickCommands.commands.eventSource.showSubjects',
      section: 'actions',
      value: 'showSubjects',
    };

    const start: CommandResultEntry = {
      label: 'quickCommands.commands.eventSource.start',
      section: 'actions',
      value: 'start',
    };

    const stop: CommandResultEntry = {
      label: 'quickCommands.commands.eventSource.stop',
      section: 'actions',
      value: 'stop',
    };

    const stopReconnecting: CommandResultEntry = {
      label: 'quickCommands.commands.eventSource.stopReconnecting',
      section: 'actions',
      value: 'stopReconnecting',
    };

    if (state === EventSourceConnectionState.Connected) {
      return [restart, stop, showSubjects];
    } else if (state === EventSourceConnectionState.WaitingToReconnect) {
      return [stopReconnecting, start];
    } else if (state === EventSourceConnectionState.Connecting) {
      return [reconnecting];
    } else {
      return [start];
    }
  }
}

type SupportedCommands = 'restart' | 'start' | 'stop' | 'stopReconnecting' | 'showSubjects';
